import { OTP_PATTERNS } from "@protontech/autofill/constants/heuristics";
import { feature, featureScaled, flattenFeatures } from "@protontech/autofill/features/feature";
import {
  field,
  fieldAttrs,
  fieldAutocomplete,
  fieldCC,
  fieldForm,
  fieldFormFeats,
  fieldIdentity,
  fieldLabel,
  fieldNext,
  fieldPrev,
  fieldSearch,
  fieldText,
  maxLength,
  pattern,
  placeholder,
  type,
} from "@protontech/autofill/features/v1/abstract.field";
import { any } from "@protontech/autofill/utils/combinators";
import { getNodeRect } from "@protontech/autofill/utils/dom";
import { matchAuthenticator, matchOtpAttr, matchOtpFieldOutlier, matchTwoFa } from "@protontech/autofill/utils/re";

type SiblingMatchOptions = { type: string | null; rect: ReturnType<typeof getNodeRect> };

export const matchSibling = (sibling: HTMLElement | null, match: SiblingMatchOptions) => {
  if (!sibling) return false;
  if (sibling?.getAttribute("type") !== match.type) return false;

  const maxLen = sibling.getAttribute("maxlength");
  if (maxLen && parseInt(maxLen, 10) > 1) return false;

  const minLen = sibling.getAttribute("minlength");
  if (minLen && parseInt(minLen) > 1) return false;

  const { top, bottom, width, area } = getNodeRect(sibling);

  /** Likely not a grouped input if too wide */
  if (width >= 100) return false;

  const topMatch = Math.abs(match.rect.top - top) <= 2;
  const bottomMatch = Math.abs(match.rect.bottom - bottom) <= 2;
  const aligned = topMatch && bottomMatch;
  if (!aligned) return false;

  /** Account for possible borders on active input */
  const areaMatch = Math.abs(match.rect.area - area) < 16;
  if (!areaMatch) return false;

  return true;
};

/* FORM DATA */
export const searchForm = feature("searchForm", { fieldFormFeats }, (p) => Boolean(p.fieldFormFeats?.searchForm));
export const formComplexity = feature("formComplexity", { fieldFormFeats }, (p) => p.fieldFormFeats?.formComplexityScaled ?? 0);
export const formMFA = feature("formMFA", { fieldFormFeats }, (p) => Boolean(p.fieldFormFeats?.formMFA));
export const formAuthenticator = feature("formAuthenticator", { fieldFormFeats }, (p) => Boolean(p.fieldFormFeats?.formTextAuthenticator));
export const mfaCandidates = feature("mfaCandidates", { fieldFormFeats }, (p) => p.fieldFormFeats?.formInputMFACandidates ?? 0);
export const outlierForm = feature("outlierForm", { fieldFormFeats }, (p) => Boolean(p.fieldFormFeats?.formOTPOutlier));
export const outlierLink = feature("outlierLink", { fieldFormFeats }, (p) => Boolean(p.fieldFormFeats?.linkOTPOutlier));
export const outlierHeadings = feature("outlierHeadings", { fieldFormFeats }, (p) => Boolean(p.fieldFormFeats?.headingsOTPOutlier));

/* ATTRIBUTES */
export const numericMode = feature("numericMode", { field }, (p) => p.field.inputMode === "numeric");
export const patternOTP = feature("patternOTP", { fieldPattern: pattern, numericMode }, (p) => !p.numericMode && OTP_PATTERNS.some((pattern) => p.pattern?.trim() === pattern));
export const nameMatch = feature("nameMatch", { field }, (p) => ["code", "token", "otp", "otc", "totp"].some((match) => p.field.name === match));
export const idMatch = feature("idMatch", { field }, (p) => ["code", "token", "otp", "otc", "totp"].some((match) => p.field.id === match));
export const autocompleteOff = feature("autocompleteOff", { fieldAutocomplete }, (p) => p.fieldAutocomplete !== undefined && (p.fieldAutocomplete === "off" || p.fieldAutocomplete === "false"));
export const autocompleteOTC = feature("autocompleteOTC", { fieldAutocomplete }, (p) => p.fieldAutocomplete?.includes("one-time-code") ?? false);
export const maxLen = feature("maxLen", { fieldMaxLength: maxLength }, (p) => (p.maxLength ? parseInt(p.maxLength, 10) : null));

/* OTP INPUT GROUP */
export const singleInput = feature("singleInput", { mfaCandidates }, (p) => p.mfaCandidates === 1);
export const rect = feature("rect", { field }, (p) => getNodeRect(p.field));
export const similarBefore = feature("similarBefore", { type, fieldPrev, rect }, (p) => matchSibling(p.fieldPrev, p));
export const similarAfter = feature("similarAfter", { type, fieldNext, rect }, (p) => matchSibling(p.fieldNext, p));
export const siblingOfInterest = feature("siblingOfInterest", { similarBefore, similarAfter }, (p) => p.similarBefore || p.similarAfter);

/* HAYSTACKS */
export const attrOTP = feature("attrOTP", { fieldAttrs }, (p) => any(matchOtpAttr)(p.fieldAttrs));
export const attrMFA = feature("attrMFA", { fieldAttrs }, (p) => any(matchTwoFa)(p.fieldAttrs));
export const attrOutlier = feature("attrOutlier", { fieldAttrs }, (p) => any(matchOtpFieldOutlier)(p.fieldAttrs));
export const textOTP = feature("textOTP", { fieldText }, (p) => matchOtpAttr(p.fieldText));
export const textMFA = feature("textMFA", { fieldText }, (p) => matchTwoFa(p.fieldText));
export const textAuthenticator = feature("textAuthenticator", { fieldText }, (p) => matchAuthenticator(p.fieldText));
export const labelOTP = feature("labelOTP", { fieldLabel }, (p) => matchOtpAttr(p.fieldLabel));
export const labelMFA = feature("labelMFA", { fieldLabel }, (p) => matchTwoFa(p.fieldLabel));
export const labelAuthenticator = feature("labelAuthenticator", { fieldLabel }, (p) => matchAuthenticator(p.fieldLabel));
export const labelOutlier = feature("labelOutlier", { fieldLabel }, (p) => matchOtpFieldOutlier(p.fieldLabel));

export const mfa = feature("mfa", { formMFA, labelMFA, textMFA, attrMFA }, (p) => p.formMFA || p.labelMFA || p.textMFA || p.attrMFA);
export const otp = feature("otp", { attrOTP, textOTP, labelOTP }, (p) => p.attrOTP || p.textOTP || p.labelOTP);

/* OUTLIER */
export const outlierCandidate = feature("outlierCandidate", { mfa, otp }, (p) => p.mfa || p.otp);
export const outlierMaxLength = feature("outlierMaxLength", { maxLen }, (p) => Boolean(p.maxLen && (p.maxLen > 20 || (p.maxLen > 1 && p.maxLen < 5))));
export const outlierInputCount = feature("outlierInputCount", { mfaCandidates }, (p) => !(p.mfaCandidates === 1 || p.mfaCandidates === 6 || p.mfaCandidates === 5));

export const outlierEmailMatchRaw = feature("outlierEmailMatch", { outlierCandidate, fieldForm }, (p) => {
  if (!p.outlierCandidate) return 0;
  return (p.fieldForm?.element.innerText ?? "").match(/[a-z0-9*._-]@/gi)?.length ?? 0;
});

export const outlierEmailMatchScaled = featureScaled(outlierEmailMatchRaw, 0, 2);
export const outlierField = feature("outlierField", { attrOutlier, labelOutlier, fieldPlaceholder: placeholder }, (p) => p.attrOutlier || p.labelOutlier || p.placeholder.includes("://"));
export const outlierSearch = feature("outlierSearch", { isSearchField: fieldSearch, searchForm }, (p) => p.isSearchField || p.searchForm);

export const outlierAutocomplete = feature(
  "outlierAutocomplete",
  { outlierCandidate, autocompleteOff, autocompleteOTC, fieldAutocomplete },
  (p) => p.outlierCandidate && p.fieldAutocomplete !== undefined && !p.autocompleteOff && !p.autocompleteOTC,
);

export const otpFeatures = flattenFeatures({
  attrMFA,
  attrOTP,
  autocompleteOTC,
  fieldCC,
  fieldIdentity,
  formAuthenticator,
  formComplexity,
  formMFA,
  idMatch,
  labelAuthenticator,
  labelMFA,
  labelOTP,
  nameMatch,
  numericMode,
  outlierAutocomplete,
  outlierEmailMatchScaled,
  outlierField,
  outlierForm,
  outlierHeadings,
  outlierInputCount,
  outlierLink,
  outlierMaxLength,
  outlierSearch,
  patternOTP,
  siblingOfInterest,
  singleInput,
  textAuthenticator,
  textMFA,
});
